function renderSubmissionDrawerEmailField({ lbl, name }: { lbl: string; name: string }): string {
  return `              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">${lbl}</p>
                <p className="text-sm">
                  {submission.${name} ? (
                    <a href={\`mailto:\${submission.${name}}\`} className="text-primary hover:underline">{submission.${name}}</a>
                  ) : '-'}
                </p>
              </div>`
}

function renderSubmissionDrawerMultilineField({
  lbl,
  name
}: {
  lbl: string
  name: string
}): string {
  return `              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">${lbl}</p>
                <p className="text-sm whitespace-pre-wrap">{submission.${name} || '-'}</p>
              </div>`
}

function renderSubmissionDrawerBooleanField({ lbl, name }: { lbl: string; name: string }): string {
  return `              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">${lbl}</p>
                <p className="text-sm">{submission.${name} ? 'Yes' : 'No'}</p>
              </div>`
}

function renderSubmissionDrawerDateField({ lbl, name }: { lbl: string; name: string }): string {
  return `              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">${lbl}</p>
                <p className="text-sm">{submission.${name} ? new Date(submission.${name}).toLocaleDateString() : '-'}</p>
              </div>`
}

function renderSubmissionDrawerUrlField({ lbl, name }: { lbl: string; name: string }): string {
  return `              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">${lbl}</p>
                <p className="text-sm">
                  {submission.${name} ? (
                    <a href={submission.${name}} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">{submission.${name}}</a>
                  ) : '-'}
                </p>
              </div>`
}

function renderSubmissionDrawerListField({ lbl, name }: { lbl: string; name: string }): string {
  return `              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">${lbl}</p>
                <div className="text-sm">
                  {Array.isArray(submission.${name}) && submission.${name}.length > 0 ? (
                    <ul className="list-inside list-disc space-y-1">
                      {submission.${name}.map((item: unknown) => {
                        const itemLabel = typeof item === 'string'
                          ? item
                          : item && typeof item === 'object'
                            ? Object.entries(item).map(([key, value]) => \`\${key}: \${String(value)}\`).join(', ')
                            : String(item)
                        return <li key={itemLabel}>{itemLabel}</li>
                      })}
                    </ul>
                  ) : '-'}
                </div>
              </div>`
}

function renderSubmissionDrawerValueField({ lbl, name }: { lbl: string; name: string }): string {
  return `              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">${lbl}</p>
                <p className="text-sm">{submission.${name} ?? '-'}</p>
              </div>`
}

function renderSubmissionDrawerCustomFieldsSection(): string {
  return `
              {submission.customFields && Object.keys(submission.customFields).length > 0 && (
                <div className="space-y-3 border-t pt-4">
                  <p className="text-sm font-semibold text-muted-foreground">Custom Fields</p>
                  {Object.entries(submission.customFields).map(([key, value]) => (
                    <div key={key} className="space-y-1">
                      <p className="text-sm font-medium capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</p>
                      <p className="text-sm">{value !== null && value !== undefined ? String(value) : '-'}</p>
                    </div>
                  ))}
                </div>
              )}`
}

function renderSubmissionDrawer({
  pascal,
  kebab,
  label,
  fieldItems,
  customFieldsSection
}: {
  pascal: string
  kebab: string
  label: string
  fieldItems: string
  customFieldsSection: string
}): string {
  return `'use client'

import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle
} from '@admin/components/ui/drawer'
import { ScrollArea } from '@admin/components/ui/scroll-area'
import { use${pascal}Submission } from '@admin/hooks/use-${kebab}-form'

interface ${pascal}SubmissionDrawerProps {
  open: boolean
  submissionId: string | null
  onOpenChange: (open: boolean) => void
}

function formatDateTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'medium'
  }).format(date)
}

export function ${pascal}SubmissionDrawer({
  open,
  submissionId,
  onOpenChange
}: ${pascal}SubmissionDrawerProps) {
  const { data: submission, error, isPending } = use${pascal}Submission(
    open ? submissionId : null
  )
  const description = submission
    ? \`Submitted \${formatDateTime(submission.submittedAt)}\`
    : submissionId
      ? \`Submission \${submissionId}\`
      : 'No submission selected'

  return (
    <Drawer swipeDirection="right" open={open} onOpenChange={onOpenChange} modal>
      <DrawerContent className="h-full w-full overflow-hidden data-[swipe-direction=right]:max-w-xl">
        <DrawerHeader>
          <DrawerTitle>${label} Submission</DrawerTitle>
          <DrawerDescription>{description}</DrawerDescription>
        </DrawerHeader>
        <ScrollArea className="min-h-0 flex-1">
          {isPending ? (
            <div className="p-4 text-sm text-muted-foreground">Loading submission…</div>
          ) : error ? (
            <div className="p-4 text-sm text-destructive">
              Error loading submission: {error.message}
            </div>
          ) : submission ? (
            <div className="p-4">
              <div className="space-y-4 rounded-sm border border-border bg-card p-4">
${fieldItems}${customFieldsSection}
                <div className="space-y-1 border-t pt-4">
                  <p className="text-sm font-medium text-muted-foreground">Submitted At</p>
                  <p className="text-sm">{formatDateTime(submission.submittedAt)}</p>
                </div>
              </div>
            </div>
          ) : submissionId ? (
            <div className="p-4 text-sm text-muted-foreground">Submission not found.</div>
          ) : null}
        </ScrollArea>
      </DrawerContent>
    </Drawer>
  )
}
`
}

export function renderAdminSubmissionDrawerRenderer(
  request:
    | {
        kind: 'renderSubmissionDrawerEmailField'
        props: {
          lbl: string
          name: string
        }
      }
    | {
        kind: 'renderSubmissionDrawerMultilineField'
        props: {
          lbl: string
          name: string
        }
      }
    | {
        kind: 'renderSubmissionDrawerBooleanField'
        props: {
          lbl: string
          name: string
        }
      }
    | {
        kind: 'renderSubmissionDrawerDateField'
        props: {
          lbl: string
          name: string
        }
      }
    | {
        kind: 'renderSubmissionDrawerUrlField'
        props: {
          lbl: string
          name: string
        }
      }
    | {
        kind: 'renderSubmissionDrawerListField'
        props: {
          lbl: string
          name: string
        }
      }
    | {
        kind: 'renderSubmissionDrawerValueField'
        props: {
          lbl: string
          name: string
        }
      }
    | { kind: 'renderSubmissionDrawerCustomFieldsSection' }
    | {
        kind: 'renderSubmissionDrawer'
        props: {
          pascal: string
          kebab: string
          label: string
          fieldItems: string
          customFieldsSection: string
        }
      }
): string {
  switch (request.kind) {
    case 'renderSubmissionDrawerEmailField':
      return renderSubmissionDrawerEmailField(request.props)
    case 'renderSubmissionDrawerMultilineField':
      return renderSubmissionDrawerMultilineField(request.props)
    case 'renderSubmissionDrawerBooleanField':
      return renderSubmissionDrawerBooleanField(request.props)
    case 'renderSubmissionDrawerDateField':
      return renderSubmissionDrawerDateField(request.props)
    case 'renderSubmissionDrawerUrlField':
      return renderSubmissionDrawerUrlField(request.props)
    case 'renderSubmissionDrawerListField':
      return renderSubmissionDrawerListField(request.props)
    case 'renderSubmissionDrawerValueField':
      return renderSubmissionDrawerValueField(request.props)
    case 'renderSubmissionDrawerCustomFieldsSection':
      return renderSubmissionDrawerCustomFieldsSection()
    case 'renderSubmissionDrawer':
      return renderSubmissionDrawer(request.props)
  }

  throw new Error('Unsupported renderer request')
}
